import React, { useMemo, useState, useEffect } from 'react';
import { Siswa } from '../../types/index.js';
import { Wallet, TrendingUp, TrendingDown, ArrowRight, Activity, ShieldCheck, PieChart, Users, AlertCircle, Check, X as XIcon, Settings2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DashboardTabunganViewProps {
  siswaList: Siswa[];
}

export const DashboardTabunganView: React.FC<DashboardTabunganViewProps> = ({ siswaList }) => {
  const navigate = useNavigate();

  const [rules, setRules] = useState({
    limitPenarikan: localStorage.getItem('tabungan_limitPenarikan') === 'true',
    limitPenarikanAmount: Number(localStorage.getItem('tabungan_limitPenarikanAmount')) || 100000,
    rekeningDorman: localStorage.getItem('tabungan_rekeningDorman') === 'true',
    minimumSaldo: localStorage.getItem('tabungan_minimumSaldo') === 'true',
    minimumSaldoAmount: Number(localStorage.getItem('tabungan_minimumSaldoAmount')) || 10000
  });

  const toggleRule = (ruleKey: keyof typeof rules) => {
    if (ruleKey === 'limitPenarikanAmount' || ruleKey === 'minimumSaldoAmount') return;
    const newValue = !rules[ruleKey as keyof typeof rules];
    localStorage.setItem(`tabungan_${String(ruleKey)}`, String(newValue));
    setRules(prev => ({ ...prev, [ruleKey]: newValue }));
  };

  const changeAmount = (ruleKey: 'limitPenarikanAmount' | 'minimumSaldoAmount', val: number) => {
    localStorage.setItem(`tabungan_${ruleKey}`, String(val));
    setRules(prev => ({ ...prev, [ruleKey]: val }));
  };

  // Basic Analytics
  const totalSiswaMenabung = useMemo(() => siswaList.filter(s => (s.saldoTabungan || 0) > 0).length, [siswaList]);
  const totalSaldoBank = useMemo(() => siswaList.reduce((sum, s) => sum + (s.saldoTabungan || 0), 0), [siswaList]);
  const rataRataTabungan = totalSiswaMenabung > 0 ? totalSaldoBank / totalSiswaMenabung : 0;

  // Find top savers
  const topSavers = useMemo(() => {
    return [...siswaList]
      .sort((a, b) => (b.saldoTabungan || 0) - (a.saldoTabungan || 0))
      .slice(0, 5);
  }, [siswaList]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Banner */}
      <div className="bg-gradient-to-r from-emerald-800 to-teal-900 rounded-2xl p-6 text-white shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-emerald-400 text-emerald-950 text-[10px] font-extrabold px-2 py-0.5 rounded uppercase tracking-widest">
              Core Banking System
            </span>
            <span className="text-xs text-emerald-200"><ShieldCheck className="w-3.5 h-3.5 inline mr-1"/> Tabungan Siswa Terlindungi</span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white mb-2">
            Dashboard Tabungan Siswa
          </h1>
          <p className="text-sm text-emerald-100 max-w-xl leading-relaxed">
            Pusat monitoring likuiditas tabungan siswa, analitik deposito, dan pengawasan rekening (mutasi kasir) secara real-time.
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={() => navigate('/tabungan/mutasi')}
            className="flex items-center justify-center gap-2 bg-emerald-400 hover:bg-emerald-300 text-emerald-950 text-sm font-bold px-5 py-3 rounded-xl shadow-sm transition"
          >
            <Wallet className="w-4 h-4" />
            <span>Mutasi & Transaksi</span>
          </button>
          <button
            onClick={() => navigate('/report/tabungan')}
            className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-bold px-5 py-3 rounded-xl border border-white/20 transition"
          >
            <PieChart className="w-4 h-4" />
            <span>Laporan Buku Tabungan</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
            <Wallet className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Likuiditas (AUM)</p>
            <h3 className="text-2xl font-black text-slate-900">
              Rp {totalSaldoBank.toLocaleString('id-ID')}
            </h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center shrink-0">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Rekening Aktif</p>
            <h3 className="text-2xl font-black text-slate-900">
              {totalSiswaMenabung} <span className="text-base text-slate-500 font-medium">Nasabah</span>
            </h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <Activity className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Rata-rata Saldo</p>
            <h3 className="text-2xl font-black text-slate-900">
              Rp {Math.round(rataRataTabungan).toLocaleString('id-ID')}
            </h3>
          </div>
        </div>
      </div>

      {/* Top Savers & Audit Warnings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              Top 5 Nasabah Terbesar
            </h3>
          </div>
          <div className="p-0">
            {topSavers.length > 0 ? (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="p-3 font-semibold">Nasabah</th>
                    <th className="p-3 font-semibold">Kelas</th>
                    <th className="p-3 font-semibold text-right">Saldo Saat Ini</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {topSavers.map((s, idx) => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="p-3 font-medium text-slate-800">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                            {idx + 1}
                          </span>
                          {s.nama}
                        </div>
                      </td>
                      <td className="p-3 text-slate-600 text-xs">{s.kelasNama}</td>
                      <td className="p-3 font-bold text-slate-900 text-right text-emerald-600">
                        Rp {(s.saldoTabungan || 0).toLocaleString('id-ID')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-slate-500 text-sm">Belum ada data tabungan</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-sky-500" />
              Pengaturan Aturan Perbankan (Core Banking)
            </h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex gap-3 flex-1">
                <div className={`w-8 h-8 rounded-full flex flex-col items-center justify-center shrink-0 ${rules.limitPenarikan ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                  {rules.limitPenarikan ? <Check className="w-4 h-4 text-emerald-600" /> : <XIcon className="w-4 h-4 text-slate-400" />}
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-bold text-slate-800">Limit Penarikan Harian (Default)</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed max-w-sm mb-2">
                    Membatasi penarikan dana maksimal per hari untuk mencegah pengurasan saldo secara tidak sah.
                  </p>
                  {rules.limitPenarikan && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700">Rp</span>
                      <input 
                        type="number"
                        value={rules.limitPenarikanAmount}
                        onChange={(e) => changeAmount('limitPenarikanAmount', Number(e.target.value))}
                        className="text-xs border border-slate-300 rounded px-2 py-1 w-32 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={() => toggleRule('limitPenarikan')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border shrink-0 transition ${rules.limitPenarikan ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'}`}
              >
                {rules.limitPenarikan ? 'Aktif' : 'Non Aktif'}
              </button>
            </div>

            <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex gap-3">
                <div className={`w-8 h-8 rounded-full flex flex-col items-center justify-center shrink-0 ${rules.rekeningDorman ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                  {rules.rekeningDorman ? <Check className="w-4 h-4 text-emerald-600" /> : <XIcon className="w-4 h-4 text-slate-400" />}
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-800">Deteksi Rekening Dorman</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed max-w-sm">
                    Membekukan rekening otomatis jika tidak ada transaksi selama &gt; 6 bulan untuk mengamankan saldo pasif.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => toggleRule('rekeningDorman')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border shrink-0 transition ${rules.rekeningDorman ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'}`}
              >
                {rules.rekeningDorman ? 'Aktif' : 'Non Aktif'}
              </button>
            </div>

            <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex gap-3 flex-1">
                <div className={`w-8 h-8 rounded-full flex flex-col items-center justify-center shrink-0 ${rules.minimumSaldo ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                  {rules.minimumSaldo ? <Check className="w-4 h-4 text-emerald-600" /> : <XIcon className="w-4 h-4 text-slate-400" />}
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-bold text-slate-800">Minimum Saldo Mengendap</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed max-w-sm mb-2">
                    Mewajibkan sisa saldo minimal di rekening tabungan, mencegah penarikan sampai Rp 0 agar rekening tetap aktif.
                  </p>
                  {rules.minimumSaldo && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-700">Rp</span>
                      <input 
                        type="number"
                        value={rules.minimumSaldoAmount}
                        onChange={(e) => changeAmount('minimumSaldoAmount', Number(e.target.value))}
                        className="text-xs border border-slate-300 rounded px-2 py-1 w-32 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={() => toggleRule('minimumSaldo')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border shrink-0 transition ${rules.minimumSaldo ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'}`}
              >
                {rules.minimumSaldo ? 'Aktif' : 'Non Aktif'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
